import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { newsApi } from "../api/newsApi";

export default function NewsDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, i18n } = useTranslation();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const pick = (n, base) => {
    const lang = (i18n?.language || "az").toLowerCase();
    const suffix = lang.startsWith("en") ? "En" : lang.startsWith("ru") ? "Ru" : "Az";

    const keyPascal = `${base}${suffix}`;
    const keyCamel = `${base[0].toLowerCase() + base.slice(1)}${suffix}`;

    const fallbackPascalAz = `${base}Az`;
    const fallbackCamelAz = `${base[0].toLowerCase() + base.slice(1)}Az`;

    return (
      n?.[keyCamel] ??
      n?.[keyPascal] ??
      n?.[fallbackCamelAz] ??
      n?.[fallbackPascalAz] ??
      ""
    );
  };

  const getYoutubeEmbedUrl = (input) => {
    if (!input) return "";

    try {
      let value = String(input).trim();

      if (!value) return "";

      value = value
        .replaceAll("&amp;", "&")
        .replaceAll("&#38;", "&")
        .replaceAll("&quot;", '"')
        .replaceAll("&#34;", '"')
        .replaceAll("&#39;", "'");

      const iframeSrcMatch = value.match(/src=["']([^"']+)["']/i);

      if (iframeSrcMatch?.[1]) {
        value = iframeSrcMatch[1].trim();
      }

      const directYoutubeIdMatch = value.match(
        /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?v=|watch\?.+&v=|embed\/|shorts\/|live\/))([a-zA-Z0-9_-]{11})/
      );

      if (directYoutubeIdMatch?.[1]) {
        return `https://www.youtube.com/embed/${directYoutubeIdMatch[1]}`;
      }

      if (/^[a-zA-Z0-9_-]{11}$/.test(value)) {
        return `https://www.youtube.com/embed/${value}`;
      }

      if (value.startsWith("//")) {
        value = `https:${value}`;
      }

      if (!value.startsWith("http://") && !value.startsWith("https://")) {
        value = `https://${value}`;
      }

      const parsed = new URL(value);
      const host = parsed.hostname
        .replace("www.", "")
        .replace("m.", "")
        .replace("music.", "");

      let videoId = "";

      if (host === "youtu.be") {
        videoId = parsed.pathname.split("/").filter(Boolean)[0] || "";
      }

      if (
        host === "youtube.com" ||
        host === "youtube-nocookie.com"
      ) {
        const pathParts = parsed.pathname.split("/").filter(Boolean);

        if (parsed.pathname === "/watch") {
          videoId = parsed.searchParams.get("v") || "";
        }

        if (pathParts[0] === "embed") {
          videoId = pathParts[1] || "";
        }

        if (pathParts[0] === "shorts") {
          videoId = pathParts[1] || "";
        }

        if (pathParts[0] === "live") {
          videoId = pathParts[1] || "";
        }
      }

      videoId = videoId
        .split("?")[0]
        .split("&")[0]
        .split("#")[0]
        .trim();

      if (!videoId) return "";

      return `https://www.youtube.com/embed/${videoId}`;
    } catch {
      return "";
    }
  };

  const formatDMY = (value) => {
    if (!value) return "";

    const d = new Date(value);

    if (Number.isNaN(d.getTime())) return "";

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    return `${dd}.${mm}.${yyyy}`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const res = await newsApi.getById(id);

        setItem(res.data);
      } catch (e) {
        console.error("news detail load error:", e);

        setItem(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <div className="container py-5">
          <h2 className="fw-bold mb-3">
            {t("loading") || "Yüklənir..."}
          </h2>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="container py-5">
          <h2 className="fw-bold mb-3">{t("news_not_found")}</h2>

          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary fw-bold"
              onClick={() => nav(-1)}
            >
              {t("back")}
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const newsId = item.id ?? item.Id;

  const title = pick(item, "Title");
  const content = pick(item, "Content");

  const youtubeLink = String(item.youtubeLink ?? item.YoutubeLink ?? "").trim();
  const youtubeEmbedUrl = getYoutubeEmbedUrl(youtubeLink);

  const hasYoutubeLink = youtubeLink.length > 0;
  const hasImage = Boolean(item.hasImage ?? item.HasImage);

  const manualDate =
    item.manualInsertedDate ??
    item.ManualInsertedDate ??
    item.insertedDate ??
    item.InsertedDate;

  return (
    <Layout>
      <section className="py-5">
        <div className="container">
          <nav aria-label="breadcrumb" className="mb-4">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/blog" className="fw-bold">
                  {t("news_breadcrumb")}
                </Link>
              </li>

              <li className="breadcrumb-item active" aria-current="page">
                {t("details")}
              </li>
            </ol>
          </nav>

          <div className="row g-4 align-items-start">
            <div className="col-12 col-md-5">
              <div
                className="rounded shadow-sm overflow-hidden"
                style={{
                  width: "100%",
                  height: 360,
                  background: "#f2f2f2",
                }}
              >
                {youtubeEmbedUrl ? (
                  <iframe
                    src={youtubeEmbedUrl}
                    title={title || "YouTube video"}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: 0,
                      display: "block",
                      background: "#000",
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                ) : hasYoutubeLink ? (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "grid",
                      placeItems: "center",
                      color: "#777",
                      fontWeight: 600,
                      textAlign: "center",
                      padding: 20,
                    }}
                  >
                    Video link düzgün YouTube formatında deyil.
                  </div>
                ) : hasImage ? (
                  <img
                    src={newsApi.getImageUrl(newsId)}
                    alt={title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "grid",
                      placeItems: "center",
                      color: "#777",
                      fontWeight: 600,
                    }}
                  >
                    {t("news_no_image")}
                  </div>
                )}
              </div>
            </div>

            <div className="col-12 col-md-7">
              <div
                style={{
                  minHeight: 360,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ flex: 1 }}>
                  <h1
                    className="fw-bold mb-3"
                    style={{
                      fontSize: "28px",
                      textAlign: "justify",
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {title}
                  </h1>

                  {content
                    ? content.split("\n").map((line, i) => {
                        const txt = line.trim();

                        if (!txt) return null;

                        return (
                          <p
                            key={i}
                            className="mb-2"
                            style={{
                              fontSize: "17px",
                              lineHeight: "1.6",
                              textAlign: "justify",
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            {txt}
                          </p>
                        );
                      })
                    : null}
                </div>

                <div
                  className="d-flex align-items-center justify-content-between mt-3 pt-3"
                  style={{
                    borderTop: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <div className="text-muted fw-semibold">
                    📅 {formatDMY(manualDate)}
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-secondary fw-bold"
                    onClick={() => nav(-1)}
                  >
                    {t("back")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}